import { Portal } from "../../Portal";
import { useState, cloneElement, useRef } from "react";
import { isEmpty } from "lodash";

export type TPosition = "TOP" | "LEFT" | "RIGHT" | "BOTTOM";

const getPosition = (placement: TPosition) => ({
	current: placement,
	isHorizontal() {
		return placement === "LEFT" || placement === "RIGHT";
	},
	isVertical() {
		return placement === "TOP" || placement === "BOTTOM";
	},
	flip() {
		if (this.current === "LEFT") return "RIGHT";
		if (this.current === "RIGHT") return "LEFT";
		if (this.current === "TOP") return "BOTTOM";
		if (this.current === "BOTTOM") return "TOP";
	},

	rotateBy90() {
		if (this.current === "LEFT") return "BOTTOM";
		if (this.current === "RIGHT") return "TOP";
		if (this.current === "TOP") return "LEFT";
		if (this.current === "BOTTOM") return "RIGHT";
	},
});

const getPoint = (el, tt, placement: TPosition, space) => {
	const pt = { x: 0, y: 0 };

	const elReact = el.getBoundingClientRect();

	const docPosition = {
		l: 0,
		t: 0,
		b: window.innerHeight - (tt.clientHeight + space),
		r: document.body.clientWidth - (tt.clientWidth + space),
	};

	let counter = 0;

	const recursive = (placement: TPosition) => {
		const position = getPosition(placement);

		counter++;
		switch (position.current) {
			case "LEFT":
				pt.x = elReact.left - (tt.offsetWidth + space);
				pt.y = elReact.top + (elReact.height - tt.offsetHeight) / 2;
				break;

			case "RIGHT":
				pt.x = elReact.right + space;
				pt.y = elReact.top + (elReact.height - tt.offsetHeight) / 2;
				break;

			case "TOP":
				pt.x = elReact.left + (elReact.width - tt.offsetWidth) / 2;
				pt.y = elReact.top - (tt.offsetHeight + space);
				break;

			default:
				pt.y = elReact.bottom + space;
				pt.x = elReact.left + (elReact.width - tt.offsetWidth) / 2;
		}

		if (counter < 2) {
			if (
				(position.isHorizontal() &&
					(pt.x < docPosition.l || pt.x > docPosition.r)) ||
				(position.isVertical() &&
					(pt.y < docPosition.t || pt.y > docPosition.b))
			) {
				return recursive(position.flip());
			}
		}
		return pt;
	};

	return recursive(placement);
};

export const Tooltip = ({ metricDefinition, children }) => {
	const space = 10;

	const mf = metricDefinition as IMetricDefinition;

	const [show, setShow] = useState(0);

	const posRef = useRef({ x: 0, y: 0 });
	const tooltipRef = useRef(null);

	const isDisabled = isEmpty(mf);

	const handleMOver = (e) => {
		posRef.current = getPoint(
			e.currentTarget,
			tooltipRef.current,
			"RIGHT",
			space
		);
		setShow(1);
	};

	const handleMOut = () => setShow(0);

	return (
		<>
			{isDisabled
				? children
				: cloneElement(children, {
						onMouseOver: handleMOver,
						onMouseOut: handleMOut,
				  })}
			{isDisabled || (
				<Portal key={metricDefinition.header + 10}>
					<div
						ref={tooltipRef}
						className="react-component tooltip-wrapper"
						style={{
							top: `${posRef.current.y}px`,
							left: `${posRef.current.x}px`,
							opacity: show,
						}}
					>
						{show && <MetricDefinition metricDefinition={mf} />}
					</div>
				</Portal>
			)}
		</>
	);
};

const MetricDefinition = ({
	metricDefinition: { header, subHeader, definition },
}) => {
	return (
		<div className="zf-border-r-xs zf-background-black zf-padding-s zf-text-white zf-text-xs zf-border-m zf-tooltip">
			<div className="zf-text-bold zf-padding-b-xxxs align-item align-item__both-end">
				{header}
			</div>
			<div className="zf-padding-b-xxxs zf-margin-b-xl">{subHeader}</div>
			<div className="tt-info">{definition}</div>
		</div>
	);
};

export interface IMetricDefinition {
	header: string;
	subHeader: string;
	definition: string;
}
